import { faFileExport, faColumns, faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ChangeEvent, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Spinner, Dropdown } from 'react-bootstrap';
import ColumnVisibilityToggle from 'components/common/ColumnVisibilityToggle';
import Button from 'components/base/Button';
import FilterTab, { FilterTabItem } from 'components/common/FilterTab';
import OrdersTable, { ordersTableColumns, createOrdersTableColumns } from 'components/tables/OrdersTable';
import useAdvanceTable from 'hooks/products/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { PaginationState, VisibilityState } from '@tanstack/react-table';
import { Order as OrderType } from 'data/e-commerce/orders';
import { FilterMenu } from 'components/common/FilterButtonGroup';
import FilterButtonGroup from 'components/common/FilterButtonGroup';
import { GET_ALL_PAYMENT_AND_SHIPPING_METHODS } from 'api/graphql/queries/order/getAllPaymentAndShippingMethods';

// Import our custom components
import ToastManager from 'components/orders/ToastManager';
import BulkStatusUpdateButton from 'components/orders/BulkStatusUpdateButton';
import ErrorNotification from 'components/orders/ErrorNotification';
import PickingListButton from 'components/orders/PickingListButton';
import InvoiceButton from 'components/orders/InvoiceButton';
import OrderFilters from 'components/orders/OrderFilters';
import { useOrdersData } from 'hooks/orders/useOrdersData';

// Import SCSS for animations
import 'assets/scss/orders/animations.scss';

/**
 * Orders Component - Displays a list of orders with filtering, sorting, and bulk operations
 */
const Orders = () => {
  // Initialize translation
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get navigation state filters
  const navigationFilters = location.state?.filter || {};
  const navigationPage = location.state?.page || 1;
  const navigationPageSize = location.state?.pageSize || 10;
  
  // Fetch payment and shipping methods
  const { data: methodsData } = useQuery(GET_ALL_PAYMENT_AND_SHIPPING_METHODS, {
    fetchPolicy: 'cache-first',
  });

  // State for column visibility
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    orderId: false, // Hide order ID column by default
    total: true,
    total_seller_amount: true,
    total_commission: true,
    customer: false,
    payment_status: false, // Hide payment status column by default
    payment_method: true,
    state: true, // Show state column by default
    fulfilment_status: true,
    delivery_type: false,
    date: true,
    order_number: true,
    transaction_link: true // Show transaction link column by default
  });

  // Search states
  const [customerNameSearch, setCustomerNameSearch] = useState("");
  const [orderNumberSearch, setOrderNumberSearch] = useState("");
  const [submittedCustomerName, setSubmittedCustomerName] = useState("");
  const [submittedOrderNumber, setSubmittedOrderNumber] = useState("");

  // Date filter states
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  // Filter states - Initialize with navigation filters if available
  const [activeOrderStatus, setActiveOrderStatus] = useState<string | string[] | "all">(
    navigationFilters.order_status || "all"
  );
  const [activeState, setActiveState] = useState<string | string[] | "all">(
    Array.isArray(navigationFilters.state) ? navigationFilters.state[0] : navigationFilters.state || "all"
  );
  const [activePaymentStatus, setActivePaymentStatus] = useState<string | "all">("all");
  const [activeShippingMethod, setActiveShippingMethod] = useState<string | "all">("all");

  // Filter label states with memoized values that update when language changes
  const [activeOrderStatusLabel, setActiveOrderStatusLabel] = useState<string>(t('orderStatusLabel'));
  const [activeStateLabel, setActiveStateLabel] = useState<string>(t('state'));
  const [activePaymentStatusLabel, setActivePaymentStatusLabel] = useState<string>(t('paymentStatus'));
  const [activeShippingMethodLabel, setActiveShippingMethodLabel] = useState<string>(t('shippingMethod'));

  // Update label states when language changes
  useEffect(() => {
    setActiveOrderStatusLabel(t('orderStatusLabel'));
    setActiveStateLabel(t('state'));
    setActivePaymentStatusLabel(t('paymentStatus'));
    setActiveShippingMethodLabel(t('shippingMethod'));
  }, [t]);

  // Handle navigation filters on mount
  useEffect(() => {
    if (Object.keys(navigationFilters).length > 0) {
    // Handle order_status filter from navigation
    if (navigationFilters.order_status) {
      if (Array.isArray(navigationFilters.order_status)) {
        if (navigationFilters.order_status.includes('Ready_to_return')) {
          setActiveOrderStatus('Ready_to_return');
          setActiveOrderStatusLabel(`${t('orderStatusLabel')}: ${t('orderStatus.ready_to_return')}`);
        } else if (navigationFilters.order_status.includes('processing')) {
          setActiveOrderStatus('processing');
          setActiveOrderStatusLabel(`${t('orderStatusLabel')}: ${t('orderStatus.processing')}`);
        } else if (navigationFilters.order_status.includes('complete')) {
          setActiveOrderStatus('complete');
          setActiveOrderStatusLabel(`${t('orderStatusLabel')}: ${t('orderStatus.completed')}`);
        }
      }
    }
    
    // Handle state filter from navigation (legacy support)
    if (navigationFilters.state) {
      if (Array.isArray(navigationFilters.state)) {
        if (navigationFilters.state.includes('processing')) {
          setActiveState('processing');
          setActiveStateLabel(`${t('state')}: ${t('orderStatus.processing')}`);
        } else if (navigationFilters.state.includes('complete')) {
          setActiveState('complete');
          setActiveStateLabel(`${t('state')}: ${t('orderStatus.completed')}`);
        }
      }
    }
    
    // Handle order_number filter from navigation
    if (navigationFilters.order_number) {
      setOrderNumberSearch(navigationFilters.order_number);
      setSubmittedOrderNumber(navigationFilters.order_number);
    }
      
      // Clear navigation state to prevent filters from persisting on refresh
      setTimeout(() => {
        navigate('/orders', { replace: true, state: {} });
      }, 100);
    }
  }, []); // Run only on mount

  // Sort states
  const [sortOrderNumber, setSortOrderNumber] = useState<number | null>(null);
  const [sortOrderDate, setSortOrderDate] = useState<number | null>(null);
  const [sortTotalAmount, setSortTotalAmount] = useState<number | null>(null);

  // Error states
  const [pickupListError, setPickupListError] = useState<string | null>(null);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const [statusUpdateError, setStatusUpdateError] = useState<string | null>(null);

  // Selected rows state
  const [selectedRows, setSelectedRows] = useState<OrderType[]>([]);

  // Helper function to check if an order is eligible for picking list
  const isOrderEligibleForPickingList = useCallback((order: OrderType): boolean => {
    // Check shipping type first - FBW orders are not eligible
    if (order.shipping_type === 'fbw') {
      return false;
    }
    
    // Check fulfillment status - map status labels to eligibility
    const statusLabel = order.fulfilment_status?.label?.toLowerCase() || '';
    
    const eligibleStatuses = [
      'processing',
      'en_cour_de_preparation',
      'being prepared', 
      'prepared',
      'transit',
      'en_cour_d_enlevement',
      'in transit',
      'completed',
      'complete',
      'enlever',
      'collected'
    ];
    
    return eligibleStatuses.some(status => statusLabel.includes(status));
  }, []);

  // Status button states
  const [showStatusButton, setShowStatusButton] = useState(false);
  const [uniformStatus, setUniformStatus] = useState<string | null>(null);
  const [targetStatus, setTargetStatus] = useState<string | null>(null);

  // Method states
  const [paymentMethods, setPaymentMethods] = useState<{ code: string, title: string }[]>([]);
  const [shippingMethods, setShippingMethods] = useState<{ code: string, carrier_title: string, method_title: string }[]>([]);

  // Memoize the filter options to prevent unnecessary rerenders
  const filterOptions = useMemo(() => ({
    customerName: submittedCustomerName,
    orderNumber: submittedOrderNumber,
    orderStatus: activeOrderStatus === "all" ? undefined : activeOrderStatus,
    state: activeState === "all" ? undefined : activeState,
    paymentStatus: activePaymentStatus === "all" ? undefined : activePaymentStatus,
    shippingMethod: activeShippingMethod === "all" ? undefined : activeShippingMethod,
    fromDate,
    toDate
  }), [
    submittedCustomerName,
    submittedOrderNumber,
    activeOrderStatus,
    activeState,
    activePaymentStatus,
    activeShippingMethod,
    fromDate,
    toDate
  ]);

  // Memoize the sort options to prevent unnecessary rerenders
  const sortOptions = useMemo(() => ({
    orderNumber: sortOrderNumber || undefined,
    orderDate: sortOrderDate || undefined,
    totalAmount: sortTotalAmount || undefined
  }), [sortOrderNumber, sortOrderDate, sortTotalAmount]);

  // Memoize the pagination options to prevent unnecessary rerenders
  const paginationOptions = useMemo(() => ({
    initialPageIndex: 0,
    initialPageSize: 10
  }), []);

  // Use our custom hook for orders data with memoized options
  const {
    tableData,
    loading,
    paginationCount,
    pagination,
    handlePageChange,
    handlePageSizeChange,
    fetchOrders,
    paymentStatusCounts,
    orderStateCounts,
    calculateSumAll
  } = useOrdersData(filterOptions, sortOptions, paginationOptions);

  // Order status filter handler
  const setOrderStatusFilter = (status: string | string[] | null, label: string) => {
    setActiveOrderStatus(status ?? "all");
    setActiveOrderStatusLabel(label);
    handlePageChange(0); // Reset to first page
  };

  // State filter handler
  const setStateFilter = (status: string | string[] | null, label: string) => {
    setActiveState(status ?? "all");
    setActiveStateLabel(label);
    handlePageChange(0); // Reset to first page
  };

  // Handler for tab clicks - uses state filter instead of order status
  const handleTabClick = useCallback((tabValue: string) => {
    // Map tab values to order state filters
    console.log(`Tab clicked: ${tabValue}`);
    if (tabValue === 'complete') {
      setStateFilter('complete', `${t('state')}: ${t('orderStatus.completed')}`);
    } else if (tabValue === 'processing') {
      setStateFilter('processing', `${t('state')}: ${t('orderStatus.processing')}`);
    } else if (tabValue === 'canceled') {
      setStateFilter('canceled', `${t('state')}: ${t('orderStatus.canceled')}`);
    } else if (tabValue === 'closed') {
      setStateFilter('closed', `${t('state')}: ${t('orderStatus.closed')}`);
    } else {
      // Reset state filter for "All" tab
      setStateFilter(null, t('state'));
    }
  }, [t, setStateFilter]);

  useEffect(() => {
    console.log(`Active State: ${activeState}`);
  }, [activeState]);

  // Payment status filter handler
  const setPaymentStatusFilter = (status: string | null, label: string) => {
    setActivePaymentStatus(status ?? "all");
    setActivePaymentStatusLabel(label);
    handlePageChange(0); // Reset to first page
  };

  // Shipping method filter handler
  const setShippingMethodFilter = (method: string | null, label: string) => {
    setActiveShippingMethod(method ?? "all");
    setActiveShippingMethodLabel(label);
    handlePageChange(0); // Reset to first page
  };

  // Tab items state with translations
  const [tabItems, setTabItems] = useState<FilterTabItem[]>([
    {
      label: t('all'),
      value: 'all',
      count: 0,
      onClick: () => handleTabClick('all')
    },
    {
      label: t('orderStatus.completed'),
      value: 'complete',
      count: 0,
      onClick: () => handleTabClick('complete')
    },
    {
      label: t('orderStatus.processing'),
      value: 'processing',
      count: 0,
      onClick: () => handleTabClick('processing')
    },
    {
      label: t('orderStatus.closed'),
      value: 'closed',
      count: 0,
      onClick: () => handleTabClick('closed')
    }
  ]);

  // // Update tab labels when language changes
  // useEffect(() => {
  //   setTabItems(prevItems => {
  //     // Create new array with updated translations
  //     return [
  //       {
  //         ...prevItems[0],
  //         label: t('all')
  //       },
  //       {
  //         ...prevItems[1],
  //         label: t('orderStatus.completed')
  //       },
  //       {
  //         ...prevItems[2],
  //         label: t('orderStatus.processing')
  //       },
  //       {
  //         ...prevItems[3],
  //         label: t('orderStatus.canceled')
  //       },
  //       {
  //         ...prevItems[4],
  //         label: t('orderStatus.closed')
  //       }
  //     ];
  //   });
  // }, [t]);

  // Sort handlers
  const toggleSortOrderNumber = () => {
    setSortOrderNumber(prev => {
      if (prev === null) return 1; // ascending
      if (prev === 1) return -1; // descending
      return null; // no sort
    });
    setSortOrderDate(null);
    setSortTotalAmount(null);
  };

  const toggleSortOrderDate = () => {
    setSortOrderDate(prev => {
      if (prev === null) return 1; // ascending
      if (prev === 1) return -1; // descending
      return null; // no sort
    });
    setSortOrderNumber(null);
    setSortTotalAmount(null);
  };

  const toggleSortTotalAmount = () => {
    setSortTotalAmount(prev => {
      if (prev === null) return 1; // ascending
      if (prev === 1) return -1; // descending
      return null; // no sort
    });
    setSortOrderNumber(null);
    setSortOrderDate(null);
  };

  // Get translated columns
  const translatedColumns = useMemo(() => {
    return createOrdersTableColumns(t);
  }, [t, i18n.language]); // Re-create when language changes

  // Set up the table with filtered columns
  const table = useAdvanceTable({
    data: tableData,
    columns: translatedColumns.filter(column => {
      const columnId = typeof column.accessorKey === 'string'
        ? column.accessorKey
        : column.id as string;
      return columnVisibility[columnId] !== false;
    }),
    pageSize: pagination.pageSize,
    pagination: true,
    sortable: true,
    rowCount: paginationCount.totalCount,
    manualPagination: true,
    onPaginationChange: (newPagination: { pageIndex: number; pageSize: number }) => {
      // If only the page index changed
      if (newPagination.pageSize === pagination.pageSize) {
        handlePageChange(newPagination.pageIndex);
      } else {
        // If the page size changed
        handlePageSizeChange(newPagination as PaginationState);
      }
    },
    selection: true
  });

  // Update payment methods when data is loaded
  useEffect(() => {
    if (methodsData?.paymentMethods?.methods) {
      setPaymentMethods(methodsData.paymentMethods.methods);
    }
    if (methodsData?.shippingMethods?.methods) {
      setShippingMethods(methodsData.shippingMethods.methods);
    }
  }, [methodsData]);

  // Watch for changes in row selection state
  useEffect(() => {
    const selectedData = table.getSelectedRowModel().rows.map(row => row.original as OrderType);
    setSelectedRows(selectedData);

    // Show status button if there are selected rows
    if (selectedData.length > 0) {
      // Check if all selected orders have the same status
      const firstStatus = selectedData[0]?.fulfilment_status?.label;
      const allSameStatus = selectedData.every(row => row.fulfilment_status?.label === firstStatus);

      setUniformStatus(allSameStatus ? firstStatus : null);
      setShowStatusButton(true);
    } else {
      setUniformStatus(null);
      setShowStatusButton(false);
    }
  }, [table.getSelectedRowModel()]);

  // Update tab items counts based on total count and order state counts
  useEffect(() => {
    if (tabItems && tabItems.length > 0) {
      setTabItems(prev => {
        const updated = [...prev];

        // Set the total count for the "All" tab
        updated[0] = { ...updated[0], count: calculateSumAll() };

        // Update counts for each tab based on order states
        if (orderStateCounts && orderStateCounts.length > 0) {
          orderStateCounts.forEach((stateCount: { code: string, count: number }) => {
            const tabIndex = updated.findIndex(tab => tab.value === stateCount.code);
            if (tabIndex > 0) { // Skip "All" tab
              updated[tabIndex] = { ...updated[tabIndex], count: stateCount.count };
            }
          });
        }

        return updated;
      });
    }
  }, [paginationCount.totalCount, orderStateCounts, calculateSumAll]); // Removed 'tabItems' from the dependency array

  // Initial fetch on component mount - run only once
  const initialFetchRef = useRef(false);
  useEffect(() => {
    if (!initialFetchRef.current) {
      fetchOrders();
      initialFetchRef.current = true;
    }
  }, [fetchOrders]);

  // Use refs to track previous filter and sort values for comparison
  const prevFilterRef = useRef({
    activeOrderStatus,
    activeState,
    activePaymentStatus,
    activeShippingMethod,
    submittedCustomerName,
    submittedOrderNumber,
    fromDate,
    toDate,
    sortOrderNumber,
    sortOrderDate,
    sortTotalAmount
  });

  // Refetch data when filters or sort options change
  useEffect(() => {
    // Check if any values have changed
    const currentFilters = {
      activeOrderStatus,
      activeState,
      activePaymentStatus,
      activeShippingMethod,
      submittedCustomerName,
      submittedOrderNumber,
      fromDate,
      toDate,
      sortOrderNumber,
      sortOrderDate,
      sortTotalAmount
    };

    // Compare with previous values
    const hasChanged = Object.keys(currentFilters).some(
      key => currentFilters[key as keyof typeof currentFilters] !==
        prevFilterRef.current[key as keyof typeof prevFilterRef.current]
    );

    if (hasChanged) {
      // Reset to first page
      handlePageChange(0);
      table.setPageIndex(0);
      // Fetch orders with new filters
      fetchOrders();

      // Update refs with current values
      prevFilterRef.current = currentFilters;
    }
  }, [
    activeOrderStatus,
    activeState,
    activePaymentStatus,
    activeShippingMethod,
    submittedCustomerName,
    submittedOrderNumber,
    fromDate,
    toDate,
    sortOrderNumber,
    sortOrderDate,
    sortTotalAmount,
    fetchOrders,
    handlePageChange,
    table
  ]);

  // Handle search input changes
  const handleCustomerNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCustomerNameSearch(e.target.value);
  };

  const handleOrderNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    setOrderNumberSearch(e.target.value);
  };

  // Handle search on Enter key
  const handleCustomerNameKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setSubmittedCustomerName(customerNameSearch);
    }
  };

  const handleOrderNumberKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setSubmittedOrderNumber(orderNumberSearch);
    }
  };

  // Define filter menus with memoized items to update when language changes
  const getOrderStatusMenuItems = useCallback(() => {
    const currentLanguage = i18n.language;

    // Helper function to get appropriate label based on language
    const getLabel = (englishLabel: string, translationKey: string): string => {
      // For English, show the original English label
      if (currentLanguage === 'en') {
        return englishLabel;
      }
      // For other languages (French, Arabic), use the translation system
      return t(translationKey);
    };

    return [
      { label: t('all'), onClick: () => setOrderStatusFilter(null, t('orderStatusLabel')) },
      {
        label: getLabel('Closed', 'orderStatus.closed'),
        onClick: () => setOrderStatusFilter('closed', `${t('orderStatusLabel')}: ${getLabel('Closed', 'orderStatus.closed')}`)
      },
      {
        label: getLabel('Processing', 'orderStatus.processing'),
        onClick: () => setOrderStatusFilter('processing', `${t('orderStatusLabel')}: ${getLabel('Processing', 'orderStatus.processing')}`)
      },
      {
        label: getLabel('Being Prepared', 'orderStatus.being_prepared'),
        onClick: () => setOrderStatusFilter('en_cour_de_preparation', `${t('orderStatusLabel')}: ${getLabel('Being Prepared', 'orderStatus.being_prepared')}`)
      },
      {
        label: getLabel('Prepared', 'orderStatus.prepared'),
        onClick: () => setOrderStatusFilter('prepared', `${t('orderStatusLabel')}: ${getLabel('Prepared', 'orderStatus.prepared')}`)
      },
      {
        label: getLabel('Completed', 'orderStatus.completed'),
        onClick: () => setOrderStatusFilter('complete', `${t('orderStatusLabel')}: ${getLabel('Completed', 'orderStatus.completed')}`)
      },
      {
        label: getLabel('Returned', 'orderStatus.returned'),
        onClick: () => setOrderStatusFilter('returned', `${t('orderStatusLabel')}: ${getLabel('Returned', 'orderStatus.returned')}`)
      },
      {
        label: getLabel('In Transit', 'orderStatus.in_transit'),
        onClick: () => setOrderStatusFilter(['transit', 'en_cour_d_enlevement'], `${t('orderStatusLabel')}: ${getLabel('In Transit', 'orderStatus.in_transit')}`)
      },
      {
        label: getLabel('Ready to Return', 'orderStatus.ready_to_return'),
        onClick: () => setOrderStatusFilter('Ready_to_return', `${t('orderStatusLabel')}: ${getLabel('Ready to Return', 'orderStatus.ready_to_return')}`)
      },
      {
        label: getLabel('Enlever', 'orderStatus.enlever'),
        onClick: () => setOrderStatusFilter('enlever', `${t('orderStatusLabel')}: ${getLabel('Enlever', 'orderStatus.enlever')}`)
      }
    ];
  }, [t, i18n.language, setOrderStatusFilter]);

  const getShippingMethodMenuItems = useCallback(() => [
    { label: t('all'), onClick: () => setShippingMethodFilter(null, t('shippingMethod')) },
    { label: t('homeDelivery'), onClick: () => setShippingMethodFilter('delivery', `${t('shippingMethod')}: ${t('homeDelivery')}`) },
    { label: t('storePickup'), onClick: () => setShippingMethodFilter('store_pickup', `${t('shippingMethod')}: ${t('storePickup')}`) }
  ], [t, setShippingMethodFilter]);

  const filterMenus: FilterMenu[] = useMemo(() => [
    {
      label: activeOrderStatusLabel,
      items: getOrderStatusMenuItems()
    },
    {
      label: activeShippingMethodLabel,
      items: getShippingMethodMenuItems()
    }
  ], [activeOrderStatusLabel, activeShippingMethodLabel, getOrderStatusMenuItems, getShippingMethodMenuItems]);

  // Convert columns to options for the column toggler
  const columnOptions = useMemo<{ id: string, label: string, visible: boolean }[]>(() => {
    return translatedColumns.map(column => {
      const columnId = typeof column.accessorKey === 'string'
        ? column.accessorKey
        : column.id as string;

      return {
        id: columnId,
        label: column.header as string,
        visible: columnVisibility[columnId] !== false
      };
    });
  }, [columnVisibility, translatedColumns]);

  // Handler for toggling column visibility
  const handleToggleColumn = (columnId: string, visible: boolean) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnId]: visible
    }));
  };

  // // Create fresh tab items directly in render to force complete refresh of labels
  // const renderTabItems = () => {
  //   console.log("Current language translations:", {
  //     all: t('tab_all'),
  //     completed: t('tab_completed'),
  //     processing: t('tab_processing'),
  //     canceled: t('tab_canceled'),
  //     closed: t('tab_closed')
  //   });

  //   return [
  //     {
  //       label: t('tab_all'),
  //       value: 'all',
  //       count: tabItems[0]?.count || 0,
  //       onClick: () => handleTabClick('all')
  //     },
  //     {
  //       label: t('tab_completed'),
  //       value: 'complete',
  //       count: tabItems[1]?.count || 0,
  //       onClick: () => handleTabClick('complete')
  //     },
  //     {
  //       label: t('tab_processing'),
  //       value: 'processing',
  //       count: tabItems[2]?.count || 0,
  //       onClick: () => handleTabClick('processing')
  //     },
  //     {
  //       label: t('tab_canceled'),
  //       value: 'canceled',
  //       count: tabItems[3]?.count || 0,
  //       onClick: () => handleTabClick('canceled')
  //     },
  //     {
  //       label: t('tab_closed'),
  //       value: 'closed',
  //       count: tabItems[4]?.count || 0,
  //       onClick: () => handleTabClick('closed')
  //     }
  //   ];
  // };

  return (
    <div>
      {/* Toast notification manager */}
      <ToastManager />

      <div className="mb-9">
        <h2 className="mb-4">{t('orders')}</h2>
        <FilterTab 
          tabItems={tabItems} 
          className="mb-2" 
          activeValue={
            activeState === "all" ? "all" :
            activeState === "complete" ? "complete" :
            activeState === "processing" ? "processing" :
            activeState === "closed" ? "closed" :
            "all"
          }
        />

        <AdvanceTableProvider {...table}>
          <div className="mb-4">
            <div className="d-flex flex-wrap align-items-start justify-content-between mb-3">
              <div className="flex-grow-1">
                {/* Order Filters Component */}
                <OrderFilters
                  customerNameSearch={customerNameSearch}
                  orderNumberSearch={orderNumberSearch}
                  onCustomerNameChange={handleCustomerNameChange}
                  onOrderNumberChange={handleOrderNumberChange}
                  onCustomerNameKeyPress={handleCustomerNameKeyPress}
                  onOrderNumberKeyPress={handleOrderNumberKeyPress}

                  fromDate={fromDate}
                  toDate={toDate}
                  onFromDateChange={(value) => setFromDate(value)}
                  onToDateChange={(value) => setToDate(value)}

                  sortOrderNumber={sortOrderNumber}
                  sortOrderDate={sortOrderDate}
                  sortTotalAmount={sortTotalAmount}
                  toggleSortOrderNumber={toggleSortOrderNumber}
                  toggleSortOrderDate={toggleSortOrderDate}
                  toggleSortTotalAmount={toggleSortTotalAmount}

                  filterMenus={filterMenus}

                  // Add the customize button inline with filters
                  customizeButton={
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="outline-warning"
                        className="px-7 flex-shrink-0 dropdown-caret-none"
                        style={{
                          backgroundColor: 'transparent',
                          border: '2px solid #ff7f00',
                          color: '#ff7f00',
                          borderRadius: '8px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        }}
                        aria-label={t('customizeTableColumns')}
                      >
                        <FontAwesomeIcon icon={faColumns} className="me-2" />
                        {t('customizeTable')}
                        <FontAwesomeIcon
                          icon={faAngleDown}
                          className="ms-2"
                          transform="down-2"
                        />
                      </Dropdown.Toggle>
                      <Dropdown.Menu
                        style={{
                          padding: '12px',
                          borderRadius: '10px',
                          boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
                          minWidth: '200px',
                          backgroundColor: 'var(--bs-body-bg, #ffffff)',
                          border: '1px solid var(--bs-border-color)'
                        }}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      >
                        {columnOptions.map((column) => (
                          <ColumnVisibilityToggle
                            key={column.id}
                            id={column.id}
                            label={column.label}
                            checked={column.visible}
                            onChange={(checked) => handleToggleColumn(column.id, checked)}
                          />
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  }

                  // Add status and action buttons
                  actionButtons={
                    <div className="d-flex gap-3">
                      {(() => {
                        const eligibleOrders = selectedRows.filter(isOrderEligibleForPickingList);
                        const fbwOrders = selectedRows.filter(row => row.shipping_type === 'fbw');
                        const notReadyOrders = selectedRows.filter(row => 
                          row.shipping_type !== 'fbw' && !isOrderEligibleForPickingList(row)
                        );

                        // Show action buttons if there are eligible orders
                        if (eligibleOrders.length > 0) {
                          return (
                            <>
                              {showStatusButton && (
                                <BulkStatusUpdateButton
                                  selectedRows={eligibleOrders}
                                  onStatusUpdate={fetchOrders}
                                  onError={(message) => setStatusUpdateError(message)}
                                />
                              )}
                              <PickingListButton
                                selectedRows={eligibleOrders}
                                onError={(message) => setPickupListError(message)}
                              />
                              <InvoiceButton
                                selectedRows={eligibleOrders}
                                onError={(message) => setInvoiceError(message)}
                              />
                            </>
                          );
                        }

                        // Show appropriate message based on why orders aren't eligible
                        if (selectedRows.length > 0) {
                          if (fbwOrders.length === selectedRows.length) {
                            // All selected orders are FBW
                            return (
                              <div className="border-start border-info border-4 bg-info-subtle p-3">
                                <div className="d-flex align-items-center">
                                  <div className="text-info me-3">
                                    <i className="fas fa-shipping-fast fs-5"></i>
                                  </div>
                                  <div>
                                    <p className="mb-1 fw-semibold text-info fs-8">Fulfilled by Wamia Orders</p>
                                    <small className="text-muted">{t('fbwOrdersNoPickingList')}</small>
                                  </div>
                                </div>
                              </div>
                            );
                          } else if (notReadyOrders.length > 0) {
                            // Some orders not ready for picking list
                            return (
                              <div className="border-start border-warning border-4 bg-warning-subtle p-3">
                                <div className="d-flex align-items-center">
                                  <div className="text-warning me-3">
                                    <i className="fas fa-clock fs-5"></i>
                                  </div>
                                  <div>
                                    <p className="mb-1 fw-semibold text-warning fs-8">Orders Not Ready</p>
                                    <small className="text-muted">{t('ordersNotReadyForPickingList')}</small>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                        }

                        return null;
                      })()}
                    </div>
                  }
                />
              </div>
            </div>
          </div>

          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
            {loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                <Spinner animation="border" role="status" variant="warning">
                  <span className="visually-hidden">{t('loading')}</span>
                </Spinner>
              </div>
            ) : (
              <OrdersTable totalCount={paginationCount.totalCount} />
            )}
          </div>
        </AdvanceTableProvider>
      </div>
    </div>
  );
};

export default Orders;
